<template>
  <div class="pptx-previewer">
    <!-- 加载状态覆盖层 -->
    <div v-if="loading" class="loading-overlay">
      <div class="loading-container">
        <el-skeleton :rows="5" animated />
        <p style="text-align: center; margin-top: 20px; color: #909399;">
          正在加载PPTX文件，请稍候...
        </p>
      </div>
    </div>

    <!-- 错误提示覆盖层 -->
    <div v-else-if="error" class="error-overlay">
      <div class="error-container">
        <el-empty description="PPTX文件加载失败">
          <template #image>
            <el-icon style="font-size: 64px; color: #909399;"><Document /></el-icon>
          </template>
          <template #description>
            <p class="error-message">{{ errorMessage }}</p>
            <div class="error-actions">
              <el-button @click="retry" type="primary">
                <el-icon><Refresh /></el-icon>
                重试
              </el-button>
            </div>
          </template>
        </el-empty>
      </div>
    </div>

    <!-- PPTX预览区域 - 使用pptx-preview -->
    <!-- 容器始终渲染，即使loading时也要存在，这样ref才能正确获取 -->
    <div class="preview-container" :style="{ visibility: loading || error ? 'hidden' : 'visible' }">
      <div ref="previewWrapper" class="preview-wrapper"></div>
    </div>
  </div>
</template>

<script>
import { init } from 'pptx-preview'
import { Document, Refresh } from '@element-plus/icons-vue'

export default {
  name: 'PPTXPreviewer',
  components: {
    Document,
    Refresh
  },
  props: {
    src: {
      type: String,
      required: true
    },
    fileName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: true,
      error: false,
      errorMessage: '',
      previewer: null,
      loadTimeout: null
    }
  },
  mounted() {
    console.log('PPTXPreviewer mounted, src:', this.src)
    // 组件挂载后再加载PPTX，确保容器已渲染
    // 容器现在始终渲染，所以应该能获取到
    this.$nextTick(() => {
      // 多等待几个tick确保DOM完全渲染
      this.$nextTick(() => {
        setTimeout(() => {
          console.log('PPTXPreviewer mounted check, src:', this.src, 'container:', !!this.$refs.previewWrapper)
          if (this.src) {
            if (this.$refs.previewWrapper) {
              console.log('容器已准备好，开始加载PPTX')
              this.loadPPTX()
            } else {
              console.warn('PPTXPreviewer: 容器仍未准备好，尝试延迟加载')
              // 再等待一下
              setTimeout(() => {
                if (this.$refs.previewWrapper) {
                  console.log('延迟后容器已准备好，开始加载PPTX')
                  this.loadPPTX()
                } else {
                  console.error('PPTXPreviewer: 容器始终未准备好')
                  this.error = true
                  this.errorMessage = '预览容器初始化失败，请刷新页面重试'
                  this.loading = false
                }
              }, 500)
            }
          } else {
            console.warn('PPTXPreviewer: src为空')
            this.error = true
            this.errorMessage = '文件URL为空'
            this.loading = false
          }
        }, 100)
      })
    })
  },
  watch: {
    src(newSrc, oldSrc) {
      console.log('PPTXPreviewer src changed:', { oldSrc, newSrc })
      // src变化时重新加载
      if (newSrc) {
        this.$nextTick(() => {
          if (this.$refs.previewWrapper) {
            this.loadPPTX()
          } else {
            setTimeout(() => {
              if (this.$refs.previewWrapper) {
                this.loadPPTX()
              } else {
                console.warn('PPTXPreviewer: 容器仍未准备好')
              }
            }, 200)
          }
        })
      }
    }
  },
  methods: {
    async loadPPTX() {
      if (!this.src) {
        this.error = true
        this.errorMessage = '文件URL为空'
        this.loading = false
        return
      }

      // 清除之前的超时
      if (this.loadTimeout) {
        clearTimeout(this.loadTimeout)
        this.loadTimeout = null
      }

      this.loading = true
      this.error = false
      this.errorMessage = ''

      try {
        console.log('PPTXPreviewer.loadPPTX 开始，src:', this.src)
        console.log('PPTXPreviewer.loadPPTX 容器引用:', this.$refs.previewWrapper)
        
        // 等待DOM元素准备好
        let container = this.$refs.previewWrapper
        
        // 如果容器不存在，等待并重试
        if (!container) {
          let retries = 0
          while (!container && retries < 30) {
            await this.$nextTick()
            await new Promise(resolve => setTimeout(resolve, 100))
            container = this.$refs.previewWrapper
            retries++
          }
        }
        
        if (!container) {
          throw new Error('预览容器未准备好，请刷新页面重试')
        }
        
        // 确保容器在DOM中
        if (!container.parentNode) {
          throw new Error('预览容器不在DOM中，请刷新页面重试')
        }

        // 获取文件数据
        const response = await fetch(this.src)
        if (!response.ok) {
          throw new Error(`HTTP错误: ${response.status}`)
        }

        const arrayBuffer = await response.arrayBuffer()
        console.log('PPTX文件加载完成，大小:', arrayBuffer.byteLength)
        
        // 清理之前的预览器
        if (this.previewer) {
          try {
            container.innerHTML = ''
          } catch (e) {
            console.warn('清理预览器失败:', e)
          }
          this.previewer = null
        }
        
        // 重新确认容器引用
        await this.$nextTick()
        container = this.$refs.previewWrapper
        if (!container || !container.parentNode) {
          throw new Error('预览容器丢失，请重试')
        }
        
        // 清空容器
        try {
          container.innerHTML = ''
        } catch (e) {
          console.warn('清空容器失败:', e)
          throw new Error('无法清空预览容器')
        }

        // 等待一下确保容器已渲染
        await new Promise(resolve => setTimeout(resolve, 200))
        
        // 再次确认容器引用
        container = this.$refs.previewWrapper
        if (!container || !container.parentNode) {
          throw new Error('预览容器在初始化前丢失，请刷新页面重试')
        }

        // 获取容器的实际尺寸
        let containerWidth = 960
        let containerHeight = 540
        
        try {
          const containerRect = container.getBoundingClientRect()
          containerWidth = containerRect.width || container.clientWidth || 960
          containerHeight = containerRect.height || container.clientHeight || 540
          
          // 确保有最小尺寸
          if (containerWidth < 400) containerWidth = 960
          if (containerHeight < 300) containerHeight = 540
        } catch (e) {
          console.warn('获取容器尺寸失败，使用默认值:', e)
        }

        console.log('初始化预览器，尺寸:', containerWidth, 'x', containerHeight)
        
        // 创建预览器
        this.previewer = init(container, {
          width: containerWidth,
          height: containerHeight
        })

        // 加载并预览文件
        console.log('开始预览PPTX文件，ArrayBuffer大小:', arrayBuffer.byteLength)
        console.log('预览器对象:', this.previewer)
        console.log('预览器方法:', typeof this.previewer.preview)
        
        // pptx-preview的preview方法
        // 根据文档，preview方法接受ArrayBuffer并直接渲染
        try {
          const previewResult = this.previewer.preview(arrayBuffer)
          console.log('preview方法调用结果:', previewResult)
          
          // 如果返回Promise，等待完成
          if (previewResult && typeof previewResult.then === 'function') {
            console.log('preview返回Promise，等待完成...')
            await previewResult
            console.log('preview Promise完成')
          }
        } catch (previewErr) {
          console.error('preview方法调用异常:', previewErr)
          throw previewErr
        }
        
        // 等待一下确保渲染完成
        // pptx-preview可能没有回调，所以等待一段时间后认为渲染完成
        console.log('等待1.5秒检查渲染结果...')
        await new Promise(resolve => setTimeout(resolve, 1500))
        
        // 检查容器是否有内容渲染出来
        const hasContent = container.children.length > 0 || container.innerHTML.trim().length > 0
        console.log('第一次容器内容检查:', {
          childrenCount: container.children.length,
          innerHTMLLength: container.innerHTML.trim().length,
          hasContent,
          firstChild: container.firstElementChild ? container.firstElementChild.tagName : 'none',
          containerClasses: container.className
        })
        
        if (hasContent) {
          console.log('PPTX预览完成 - 检测到内容')
          this.loading = false
          this.error = false
        } else {
          // 如果还是没有内容，再等待一下
          console.log('第一次检查无内容，等待3秒后再次检查...')
          await new Promise(resolve => setTimeout(resolve, 3000))
          const stillHasContent = container.children.length > 0 || container.innerHTML.trim().length > 0
          console.log('第二次容器内容检查:', {
            childrenCount: container.children.length,
            innerHTMLLength: container.innerHTML.trim().length,
            stillHasContent,
            containerHTML: container.innerHTML.substring(0, 500) // 显示前500字符用于调试
          })
          if (stillHasContent) {
            console.log('PPTX预览完成（延迟检测）')
            this.loading = false
            this.error = false
          } else {
            console.error('PPTX渲染失败 - 容器中始终没有内容')
            console.error('容器状态:', {
              container: container,
              parentNode: container.parentNode,
              offsetWidth: container.offsetWidth,
              offsetHeight: container.offsetHeight,
              style: container.style.cssText
            })
            throw new Error('PPTX文件渲染失败，容器中没有内容。请检查文件格式是否正确，或尝试刷新页面。')
          }
        }
      } catch (err) {
        console.error('PPTX加载失败:', err)
        this.loading = false
        this.error = true
        this.errorMessage = err.message || '无法加载PPTX文件，请检查文件格式或网络连接'
      }
    },
    
    retry() {
      this.loadPPTX()
    }
  },
  
  beforeUnmount() {
    // 清理预览器
    if (this.previewer && this.$refs.previewWrapper) {
      try {
        this.$refs.previewWrapper.innerHTML = ''
      } catch (e) {
        console.warn('清理预览器失败:', e)
      }
      this.previewer = null
    }
    
    // 清理超时
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout)
      this.loadTimeout = null
    }
  }
}
</script>

<style scoped>
.pptx-previewer {
  width: 100%;
  height: 100%;
  min-height: 600px;
  max-height: calc(100vh - 350px);
  display: flex;
  flex-direction: column;
  background: #f5f5f5;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  z-index: 1;
}

.preview-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  min-height: 500px;
}

.loading-overlay,
.error-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.error-message {
  color: #f56c6c;
  margin: 16px 0;
  text-align: center;
  max-width: 500px;
}

.error-actions {
  display: flex;
  gap: 12px;
  margin-top: 20px;
}

.preview-wrapper {
  flex: 1;
  overflow: auto;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 500px;
  position: relative;
  width: 100%;
  box-sizing: border-box;
  contain: layout style paint;
}

/* 确保pptx-preview内部的绝对定位元素不会溢出 */
.preview-wrapper :deep(.slide-wrapper) {
  position: relative !important;
  max-width: 100%;
  max-height: 100%;
}

.preview-wrapper :deep(.slide-container) {
  position: relative !important;
  max-width: 100%;
  max-height: 100%;
  overflow: hidden;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .pptx-previewer {
    min-height: 400px;
  }
  
  .preview-container {
    min-height: 400px;
  }
  
  .preview-wrapper {
    min-height: 400px;
  }
}
</style>
